import React from 'react';
import {
  Copy,
  CurrencyDollarSimple,
  DownloadSimple,
  FileText,
  ChartLineUp,
  MagnifyingGlass,
  NotePencil,
  Percent,
  Receipt,
  User,
  Warning,
} from 'phosphor-react';
import { FaNotEqual } from 'react-icons/fa';
import { FaFileInvoice, FaFileInvoiceDollar } from 'react-icons/fa6';
import { MdOutlineInsertPageBreak } from 'react-icons/md';
import { RiFileList3Line, RiFundsLine } from 'react-icons/ri';
import { TbPercentage, TbReportMoney } from 'react-icons/tb';

/**
 * Submódulos de Fiscal e Integração no padrão do Contábil (24/10): o hub em
 * vidro e o menu lateral (drill) leem DESTA lista, então os dois mostram os
 * mesmos itens, na mesma ordem. Antes ficavam em abas no cabeçalho, com uma
 * lista própria que já tinha divergido do hub.
 *
 * Lista explícita (e não as rotas filhas, como o Contábil) porque os dois hubs
 * têm telas fora do próprio caminho — `/modulesHome/fiscal/*` — e os `name`
 * dessas rotas são internos ("integration sequenceBreak").
 */

type Icone = React.ComponentType<{ size?: number | string; color?: string }>;

export interface ISubmoduloHub {
  title: string;
  path: string;
  Icone: Icone;
  cor: string;
  tint: string;
  descricao: string;
  badge?: string;
}

export const BASE_FISCAL = '/modulesHome/supervisor';
export const BASE_INTEGRACAO = '/modulesHome/integration';

export const SUBMODULOS_FISCAL: ISubmoduloHub[] = [
  {
    title: 'Lucro Real',
    path: '/modulesHome/supervisor/lucro-real',
    Icone: RiFileList3Line,
    cor: '#008CCC',
    tint: '#DFF3FF',
    descricao: 'Faturamento e impostos das empresas do Lucro Real',
  },
  {
    title: 'Lucro Presumido',
    path: '/modulesHome/supervisor/lucro-presumido',
    Icone: RiFileList3Line,
    cor: '#008CCC',
    tint: '#DFF3FF',
    descricao: 'Faturamento e impostos das empresas do Lucro Presumido',
  },
  {
    title: 'Sub.Limite Simples e Presumido',
    path: '/modulesHome/supervisor/taxRegime',
    Icone: TbReportMoney,
    cor: '#F2994A',
    tint: '#FFE1C6',
    descricao: 'Empresas perto do sublimite do Simples e do limite do Presumido',
  },
  {
    title: 'Simples Nacional',
    path: '/modulesHome/supervisor/simples',
    Icone: CurrencyDollarSimple,
    cor: '#4CAF50',
    tint: '#FFEB99',
    descricao: 'Faturamento e impostos das empresas do Simples Nacional',
  },
  {
    title: 'Faturamento dos Sócios',
    path: '/modulesHome/supervisor/partner',
    Icone: User,
    cor: '#37474F',
    tint: '#CFD8DC',
    descricao: 'Faturamento das empresas de cada sócio',
  },
  {
    title: '% Impostos / Faturamento',
    path: '/modulesHome/supervisor/tax-analysis',
    Icone: TbPercentage,
    cor: '#4CAF50',
    tint: '#E8F5E9',
    descricao: 'Porcentagem dos impostos (PIS, COFINS, CSLL, IRPJ, ICMS) sobre o faturamento',
  },
  {
    title: '% Simples Nacional / Faturamento',
    path: '/modulesHome/supervisor/tax-analysis-sn',
    Icone: Percent,
    cor: '#FFA000',
    tint: '#FFF8E1',
    descricao: 'Porcentagem que o Simples Nacional representa sobre o faturamento',
  },
  {
    title: 'Agente de Auditoria Lucro Presumido',
    path: '/modulesHome/fiscal/auditoria-calculo',
    Icone: MagnifyingGlass,
    cor: '#DC2626',
    tint: '#FEE2E2',
    descricao: 'Auditoria do cálculo dos impostos do Lucro Presumido',
  },
  {
    title: 'Agente EFD Contribuições',
    path: '/modulesHome/fiscal/efd-erros',
    Icone: Warning,
    cor: '#E65100',
    tint: '#FFF3E0',
    descricao: 'Erros de validação e de processamento da EFD Contribuições',
  },
  {
    title: 'ICMS',
    path: '/modulesHome/supervisor/icms',
    Icone: RiFundsLine,
    cor: '#5D7FFF',
    tint: '#E8EFFF',
    descricao: 'Acumuladores e DIFAL do ICMS',
  },
  {
    title: 'Emissão de NFS-e',
    path: '/modulesHome/supervisor/nfse-emissao',
    Icone: Receipt,
    cor: '#008CCC',
    tint: '#DFF3FF',
    descricao: 'API Nacional · Sefin',
    badge: 'NOVO',
  },
];

export const SUBMODULOS_INTEGRACAO: ISubmoduloHub[] = [
  {
    title: 'Quebra de Sequência',
    path: '/modulesHome/integration/sequenceBreak',
    Icone: MdOutlineInsertPageBreak,
    cor: '#008CCC',
    tint: '#DFF3FF',
    descricao: 'Notas com quebra na sequência de numeração',
  },
  {
    title: 'Análise de Faturamento',
    path: '/modulesHome/integration/billingAnalysis',
    Icone: FaFileInvoiceDollar,
    cor: '#219653',
    tint: '#ABEDC6',
    descricao: 'Faturamento das empresas a partir das notas',
  },
  {
    title: 'Conferência de Importações',
    path: '/modulesHome/integration/conferencia-fiscal',
    Icone: FaNotEqual,
    cor: '#ff4e2a',
    tint: '#FFE4DE',
    descricao: 'Notas recebidas x notas importadas no Domínio',
  },
  {
    title: 'Importações Diárias',
    path: '/modulesHome/integration/dailyImports',
    Icone: DownloadSimple,
    cor: '#0077B6',
    tint: '#D9F1F1',
    descricao: 'Notas importadas no Domínio, dia a dia',
  },
  {
    title: 'Lançamentos Manuais',
    path: '/modulesHome/integration/manualPostings',
    Icone: NotePencil,
    cor: '#37474F',
    tint: '#CFD8DC',
    descricao: 'Notas lançadas manualmente no Domínio',
  },
  {
    title: 'Portal Nacional',
    path: '/modulesHome/integration/nfseManagement',
    Icone: FaFileInvoice,
    cor: '#1976D2',
    tint: '#E3F2FD',
    descricao: 'NFS-e do Portal Nacional',
  },
  {
    title: 'Análise Relatórios NFS-e',
    path: '/modulesHome/integration/nfseDashboard',
    Icone: FileText,
    cor: '#0284C7',
    tint: '#E0F2FE',
    descricao: 'Relatórios de NFS-e',
  },
  {
    title: 'Variância de Notas',
    path: '/modulesHome/fiscal/variancia-nota',
    Icone: ChartLineUp,
    cor: '#7B1FA2',
    tint: '#F3E5F5',
    descricao: 'Variação dos valores das notas',
  },
  {
    title: 'Notas Duplicadas',
    path: '/modulesHome/integration/duplicateNotes',
    Icone: Copy,
    cor: '#DC2626',
    tint: '#FEE2E2',
    descricao: 'Notas em duplicidade',
  },
];

/** Base do módulo → seus submódulos (o hub e o drill do menu lateral). */
export const SUBMODULOS_POR_MODULO: Record<string, ISubmoduloHub[]> = {
  [BASE_FISCAL]: SUBMODULOS_FISCAL,
  [BASE_INTEGRACAO]: SUBMODULOS_INTEGRACAO,
};

/** A tela atual é este submódulo (ou uma tela interna dele)? */
export const ehDoSubmodulo = (path: string, pathname: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

/**
 * Base do módulo de uma tela que mora FORA do caminho dele (as de
 * `/modulesHome/fiscal/*`), para o menu lateral continuar no módulo certo.
 */
export const moduloDaTelaAvulsa = (pathname: string): string | undefined =>
  Object.keys(SUBMODULOS_POR_MODULO).find(base =>
    SUBMODULOS_POR_MODULO[base].some(
      s => !s.path.startsWith(`${base}/`) && ehDoSubmodulo(s.path, pathname),
    ),
  );
